(() => {
  "use strict";

  if (window.CCMShopifyForms?.__initialized) return;

  const QUOTE_VEHICLE_CONFIGURATION_NAME = "custom#vehicle_configuration";

  const CONSULTATION_FIELD_MAP = {
    "Consultation context": c => c.consultationContext ||
      ["Product consultation", c.productTitle, c.variantTitle, c.color].filter(Boolean).join(" | "),
    "Vehicle model": c => c.productTitle || "",
    "Variant": c => c.variantTitle || "",
    "Exterior color": c => c.color || "",
    "Product ID": c => String(c.productId || ""),
    "Variant ID": c => String(c.variantId || ""),
    "Compared vehicles": c => c.comparedVehiclesText || "",
    "Source page URL": c => c.productUrl || location.href,
    "Store market": c => c.market || "",
    "Store language": c => c.language || document.documentElement.lang || ""
  };

  const READONLY = new Set(["Consultation context"]);
  const TECHNICAL = new Set([
    "Vehicle model",
    "Variant",
    "Exterior color",
    "Price snapshot",
    "Product ID",
    "Product handle",
    "Variant ID",
    "Source page URL",
    "Store market",
    "Store language",
    "Compared vehicles"
  ]);

  let previousFocus = null;
  let successObservers = new WeakMap();

  const normalize = value => String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\*+$/g, "")
    .trim()
    .toLowerCase();

  const escapeHtml = value => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  function inferTheme() {
    const html = document.documentElement;
    const body = document.body;
    const htmlTheme = (html.getAttribute("data-theme") || html.getAttribute("data-color-scheme") || "").toLowerCase();
    const bodyTheme = (body?.getAttribute("data-theme") || body?.getAttribute("data-color-scheme") || "").toLowerCase();
    const classText = `${html.className} ${body?.className || ""}`.toLowerCase();

    if (
      htmlTheme === "light" ||
      bodyTheme === "light" ||
      classText.includes("light-theme") ||
      classText.includes("theme-light") ||
      classText.includes("is-light")
    ) return "light";

    if (
      htmlTheme === "dark" ||
      bodyTheme === "dark" ||
      classText.includes("dark-theme") ||
      classText.includes("theme-dark") ||
      classText.includes("is-dark")
    ) return "dark";

    return window.matchMedia?.("(prefers-color-scheme: light)")?.matches ? "light" : "dark";
  }

  function syncTheme() {
    const theme = inferTheme();
    document.querySelectorAll("[data-ccm-shopify-form-modal]").forEach(modal => {
      modal.dataset.ccmTheme = theme;
    });
  }

  function queryAllDeep(root, selector) {
    if (!root) return [];
    const matches = [];
    const seen = new Set();

    const visit = node => {
      if (!node || seen.has(node)) return;
      seen.add(node);

      if (node.nodeType === Node.ELEMENT_NODE && node.matches?.(selector)) {
        matches.push(node);
      }

      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach(match => {
          if (!matches.includes(match)) matches.push(match);
        });
      }

      const descendants = node.querySelectorAll ? node.querySelectorAll("*") : [];
      descendants.forEach(child => {
        if (child.shadowRoot) visit(child.shadowRoot);
      });
    };

    visit(root);
    return matches;
  }

  function queryDeep(root, selector) {
    return queryAllDeep(root, selector)[0] || null;
  }

  function associatedLabelText(control) {
    const labels = [];
    if (control.id) {
      document.querySelectorAll(`label[for="${CSS.escape(control.id)}"]`).forEach(label => labels.push(label.textContent));
    }
    if (control.labels) [...control.labels].forEach(label => labels.push(label.textContent));
    const wrappingLabel = control.closest("label");
    if (wrappingLabel) labels.push(wrappingLabel.textContent);
    return labels
      .map(value => String(value || "").replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join(" | ");
  }

  function findControlForField(formRoot, exactLabel, action = "quote") {
    if (!formRoot) return null;
    const controls = queryAllDeep(formRoot, "input,select,textarea");
    const needles = [normalize(exactLabel)].filter(Boolean);

    const byPlaceholder = controls.find(control => needles.includes(normalize(control.getAttribute("placeholder"))));
    if (byPlaceholder) return byPlaceholder;

    const byAriaLabel = controls.find(control => needles.includes(normalize(control.getAttribute("aria-label"))));
    if (byAriaLabel) return byAriaLabel;

    const byAssociatedLabel = controls.find(control => needles.includes(normalize(associatedLabelText(control))));
    if (byAssociatedLabel) return byAssociatedLabel;

    return controls.find(control => {
      const values = [
        control.getAttribute("name"),
        control.getAttribute("placeholder"),
        control.getAttribute("aria-label"),
        associatedLabelText(control)
      ].map(normalize).filter(Boolean);
      return values.some(value => needles.includes(value));
    }) || null;
  }

  function findControlByLabel(formRoot, exactLabel) {
    return findControlForField(formRoot, exactLabel);
  }

  function findQuoteVehicleConfigurationControl(modal) {
    const mount = modal.querySelector('[data-ccm-shopify-form-mount="quote"]');
    return queryAllDeep(mount, "input,select,textarea")
      .find(control => control.getAttribute("name") === QUOTE_VEHICLE_CONFIGURATION_NAME) || null;
  }

  function quoteVehicleConfiguration(context) {
    return [
      context.productTitle,
      context.variantTitle,
      context.color,
      context.priceText,
      context.productUrl || location.href
    ].filter(Boolean).join(" | ");
  }

  function setControlValue(control, value) {
    if (!control) return;

    const prototype =
      control instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype :
      control instanceof HTMLSelectElement ? HTMLSelectElement.prototype :
      HTMLInputElement.prototype;

    const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
    if (setter) setter.call(control, String(value ?? ""));
    else control.value = String(value ?? "");

    control.dispatchEvent(new Event("input", { bubbles: true }));
    control.dispatchEvent(new Event("change", { bubbles: true }));
    control.dispatchEvent(new Event("blur", { bubbles: true }));
  }

  function nearestFieldWrapper(control) {
    const labelled = control.closest("label");
    return control.closest("[data-field],.field,.form-field,fieldset,li") ||
      labelled?.parentElement ||
      control.parentElement;
  }

  function controlLabel(control) {
    const labels = [];
    if (control.id) {
      document.querySelectorAll(`label[for="${CSS.escape(control.id)}"]`).forEach(label => labels.push(label));
    }
    if (control.labels) labels.push(...control.labels);
    const wrappingLabel = control.closest("label");
    if (wrappingLabel) labels.push(wrappingLabel);
    return [...new Set(labels)]
      .map(label => label.textContent.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join(" | ");
  }

  function selectorHint(element) {
    if (!element) return "";
    if (element.id) return `#${CSS.escape(element.id)}`;
    const dataField = element.getAttribute("data-field") || element.getAttribute("data-testid");
    if (dataField) return `[data-field="${CSS.escape(dataField)}"], [data-testid="${CSS.escape(dataField)}"]`;
    const className = [...(element.classList || [])].slice(0, 3).map(name => `.${CSS.escape(name)}`).join("");
    return `${element.localName || "element"}${className}`;
  }

  function describeControl(control, form) {
    const wrapper = nearestFieldWrapper(control);
    const name = control.getAttribute("name") || "";
    const value = control.type === "password" ? "[password]" : control.value;
    const formDataValues = form && name ? new FormData(form).getAll(name).map(item => String(item)) : [];
    return {
      label: controlLabel(control),
      placeholder: control.getAttribute("placeholder") || "",
      ariaLabel: control.getAttribute("aria-label") || "",
      name,
      id: control.id || "",
      type: control.tagName.toLowerCase() === "select" ? "select" : control.getAttribute("type") || control.tagName.toLowerCase(),
      labelFor: control.id ? document.querySelector(`label[for="${CSS.escape(control.id)}"]`)?.getAttribute("for") || "" : "",
      insideRealForm: !!control.closest("form"),
      disabled: !!control.disabled,
      populated: String(value || "").trim() !== "",
      value,
      wrapper: selectorHint(wrapper),
      submittedKey: name,
      inFormData: formDataValues.includes(String(value || "")),
      formDataValue: formDataValues.join(" | ")
    };
  }

  function applyPresentation(control, label) {
    if (READONLY.has(label)) {
      if ("readOnly" in control) control.readOnly = true;
      control.setAttribute("aria-readonly", "true");
      control.classList.add("ccm-sf-readonly");
    }

    if (TECHNICAL.has(label)) {
      const wrapper = nearestFieldWrapper(control);
      if (wrapper) {
        wrapper.classList.add("ccm-sf-technical-field");
        wrapper.setAttribute("aria-hidden", "true");
      }
      control.tabIndex = -1;
    }
  }

  function hasShopifySuccessState(root) {
    const text = normalize(root?.textContent);
    return text.includes("thank") ||
      text.includes("success") ||
      text.includes("submitted") ||
      text.includes("received");
  }

  function hasShopifyFormsRoot(root) {
    return !!queryDeep(root, '[data-form-root="true"],[data-forms-id]');
  }

  function hideLoadError(loadError) {
    if (!loadError) return;
    loadError.hidden = true;
    loadError.setAttribute("aria-hidden", "true");
    loadError.classList.remove("is-visible", "is-error", "visible", "error");
  }

  function showLoadError(loadError) {
    if (!loadError) return;
    loadError.hidden = false;
    loadError.setAttribute("aria-hidden", "false");
  }

  function waitForShopifyForm(modal, action, timeoutMs = 15000) {
    const mount = modal.querySelector(`[data-ccm-shopify-form-mount="${action}"]`);

    return new Promise((resolve, reject) => {
      if (!hasShopifyFormsRoot(mount)) {
        reject(new Error(`Shopify Forms root for "${action}" is missing.`));
        return;
      }

      const ready = () => {
        const form = queryDeep(mount, "form");
        if (form) return { status: "form", form };
        if (hasShopifySuccessState(mount)) return { status: "success", form: null };
        return null;
      };

      const initial = ready();
      if (initial) {
        resolve(initial);
        return;
      }

      const observer = new MutationObserver(() => {
        const form = ready();
        if (!form) return;
        observer.disconnect();
        resolve(form);
      });

      if (mount) observer.observe(mount, { childList: true, subtree: true });

      window.setTimeout(() => {
        observer.disconnect();
        const finalState = ready();
        if (finalState) {
          resolve(finalState);
          return;
        }
        if (hasShopifyFormsRoot(mount)) {
          resolve({ status: "root", form: null });
          return;
        }
        reject(new Error(`Shopify Form "${action}" was not ready within ${timeoutMs}ms.`));
      }, timeoutMs);
    });
  }

  function populateWhenControlsArrive(modal, action, context, timeoutMs = 30000) {
    const mount = modal.querySelector(`[data-ccm-shopify-form-mount="${action}"]`);
    if (!mount) return;

    const tryPopulate = () => {
      if (action === "quote") {
        if (!findQuoteVehicleConfigurationControl(modal)) return false;
        populateQuote(modal, context);
        return true;
      }

      if (!queryDeep(mount, "form")) return false;
      populate(modal, action, context);
      return true;
    };

    if (tryPopulate()) return;

    const observer = new MutationObserver(() => {
      if (!tryPopulate()) return;
      observer.disconnect();
    });

    observer.observe(mount, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), timeoutMs);
  }

  function populate(modal, action, context) {
    const mount = modal.querySelector(`[data-ccm-shopify-form-mount="${action}"]`);
    const form = queryDeep(mount, "form");
    const fields = action === "consultation" ? CONSULTATION_FIELD_MAP : {};

    for (const [label, resolver] of Object.entries(fields)) {
      const control = findControlForField(mount, label, action);
      if (!control) {
        console.warn(`[CCM Shopify Forms] Missing field control: ${label}`);
        continue;
      }
      control.disabled = false;
      setControlValue(control, resolver(context));
      applyPresentation(control, label);

      const row = describeControl(control, form);
      if (!String(control.value || "").trim()) {
        console.warn(`[CCM Shopify Forms] Field populated empty: ${label}`, row);
      }
      if (form && control.name && !row.inFormData) {
        console.warn(`[CCM Shopify Forms] Field missing from FormData: ${label}`, row);
      }
    }
  }

  function populateQuote(modal, context) {
    const control = findQuoteVehicleConfigurationControl(modal);
    if (!control) {
      console.warn(`[CCM Shopify Forms] Missing Request Quote field: ${QUOTE_VEHICLE_CONFIGURATION_NAME}`);
      return;
    }

    const form = control.form || control.closest("form");
    const value = quoteVehicleConfiguration(context);

    control.disabled = false;
    if ("readOnly" in control) control.readOnly = true;
    control.setAttribute("aria-readonly", "true");
    control.classList.add("ccm-sf-readonly");

    setControlValue(control, value);

    if (!String(control.value || "").trim()) {
      console.warn("[CCM Shopify Forms] Request Quote vehicle configuration populated empty.", { value, context });
    }

    if (form && control.name) {
      const values = new FormData(form).getAll(control.name).map(item => String(item));
      if (!values.includes(String(value || ""))) {
        console.warn("[CCM Shopify Forms] Request Quote vehicle configuration missing from FormData.", {
          name: control.name,
          value,
          formDataValues: values
        });
      }
    }
  }

  function firstEditable(modal) {
    return [...modal.querySelectorAll("input,select,textarea,button")]
      .find(el =>
        !el.readOnly &&
        el.tabIndex !== -1 &&
        el.type !== "hidden" &&
        !el.closest(".ccm-sf-technical-field") &&
        !el.closest("[aria-hidden='true']")
      );
  }

  function updateSummary(modal, context) {
    const summary = modal.querySelector("[data-ccm-vehicle-summary]");
    const title = modal.querySelector("[data-ccm-context-title]");
    const image = modal.querySelector("[data-ccm-context-image]");
    const chips = modal.querySelector("[data-ccm-context-chips]");

    if (!summary) return;
    summary.hidden = false;

    if (title) {
      title.textContent = context.productTitle ||
        context.consultationContext ||
        "Selected vehicles";
    }

    if (image) {
      image.innerHTML = context.imageUrl
        ? `<img src="${escapeHtml(context.imageUrl)}" alt="${escapeHtml(context.productTitle || "Selected vehicle")}">`
        : "";
    }

    if (chips) {
      chips.innerHTML = [
        context.variantTitle,
        context.color,
        context.priceText
      ].filter(Boolean).map(value => `<span>${escapeHtml(value)}</span>`).join("");
    }
  }

  function emit(eventName, context) {
    window.dispatchEvent(new CustomEvent("ccm:lead", {
      detail: {
        event: eventName,
        productId: context.productId || "",
        variantId: context.variantId || "",
        leadType: eventName.includes("quote") ? "quote" : "consultation",
        source: location.pathname
      }
    }));
  }

  function openShell(modal) {
    previousFocus = document.activeElement;
    syncTheme();
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("ccm-modal-lock");
  }

  function closeShell(modal) {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("ccm-modal-lock");
    previousFocus?.focus?.();
  }

  function trapFocus(event) {
    if (event.key !== "Tab") return;
    const modal = document.querySelector("[data-ccm-shopify-form-modal].is-open");
    if (!modal) return;
    const focusable = [...modal.querySelectorAll("a[href],button,input,select,textarea,[tabindex]:not([tabindex='-1'])")]
      .filter(el => el.offsetParent !== null && !el.closest(".ccm-sf-technical-field"));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function observeSuccess(modal, action, context) {
    const mount = modal.querySelector(`[data-ccm-shopify-form-mount="${action}"]`);
    if (!mount || successObservers.has(mount)) return;
    const loadError = modal.querySelector("[data-ccm-form-load-error]");

    if (hasShopifySuccessState(mount)) {
      hideLoadError(loadError);
      emit(action === "quote" ? "quote_submitted" : "consultation_submitted", context);
      return;
    }

    const observer = new MutationObserver(() => {
      if (!hasShopifySuccessState(mount)) return;
      hideLoadError(loadError);
      observer.disconnect();
      successObservers.delete(mount);
      emit(action === "quote" ? "quote_submitted" : "consultation_submitted", context);
    });

    observer.observe(mount, { childList: true, subtree: true, characterData: true });
    successObservers.set(mount, observer);
  }

  async function prepare(action, context) {
    const modal = document.querySelector(`[data-ccm-shopify-form-modal][data-ccm-modal="${action}"]`);
    if (!modal) throw new Error(`Missing ${action} Shopify Forms modal.`);

    const loadError = modal.querySelector("[data-ccm-form-load-error]");
    hideLoadError(loadError);

    updateSummary(modal, context);
    openShell(modal);
    observeSuccess(modal, action, context);

    try {
      const readyState = await waitForShopifyForm(modal, action);
      hideLoadError(loadError);

      if (readyState.status === "form") {
        if (action === "quote") populateQuote(modal, context);
        else populate(modal, action, context);
        firstEditable(modal)?.focus();
      } else if (readyState.status === "root") {
        populateWhenControlsArrive(modal, action, context);
      }

      emit(action === "quote" ? "quote_modal_opened" : "consultation_modal_opened", context);
    } catch (error) {
      console.error(error);
      const mount = modal.querySelector(`[data-ccm-shopify-form-mount="${action}"]`);
      if (hasShopifyFormsRoot(mount) || hasShopifySuccessState(mount)) {
        hideLoadError(loadError);
      } else {
        showLoadError(loadError);
      }
    }
  }

  function currentProductContext() {
    return window.CCMVehicleState?.getCurrent?.() || {
      productUrl: location.href,
      language: document.documentElement.lang || ""
    };
  }

  function comparisonContext() {
    const selectedVehicles = window.selectedVehicles || window.CCMCompareState?.selectedVehicles || [];
    const names = selectedVehicles.map(vehicle =>
      vehicle.title || vehicle.name || vehicle.productTitle || vehicle.model || vehicle.id
    ).filter(Boolean);
    return {
      ...currentProductContext(),
      consultationContext: names.length ? `Comparison consultation | ${names.length} vehicles` : "Comparison consultation",
      comparedVehiclesText: names.join("; "),
      productUrl: location.href
    };
  }

  function handleClick(event) {
    const closeTrigger = event.target.closest("[data-ccm-modal-close]");
    if (closeTrigger) {
      const modal = closeTrigger.closest("[data-ccm-shopify-form-modal]");
      if (modal) closeShell(modal);
      return;
    }

    const quoteTrigger = event.target.closest("[data-request-quote],[data-ccm-action='quote']");
    if (quoteTrigger) {
      const context = currentProductContext();
      if (!context.productId || !context.productTitle) {
        console.warn("[CCM Shopify Forms] Incomplete vehicle state for Request Quote.", context);
      }
      prepare("quote", context);
      return;
    }

    const consultationTrigger = event.target.closest("[data-book-consultation],[data-ccm-action='consultation']");
    if (consultationTrigger) {
      const context = location.pathname.includes("compare") ? comparisonContext() : currentProductContext();
      prepare("consultation", context);
    }
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      const modal = document.querySelector("[data-ccm-shopify-form-modal].is-open");
      if (modal) closeShell(modal);
    }
    trapFocus(event);
  }

  syncTheme();
  const themeObserver = new MutationObserver(syncTheme);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme", "data-color-scheme"] });
  if (document.body) themeObserver.observe(document.body, { attributes: true, attributeFilter: ["class", "data-theme", "data-color-scheme"] });
  window.matchMedia?.("(prefers-color-scheme: light)")?.addEventListener?.("change", syncTheme);
  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);

  window.CCMShopifyForms = {
    __initialized: true,
    openQuote() {
      return prepare("quote", currentProductContext());
    },
    openConsultation(context) {
      return prepare("consultation", context || currentProductContext());
    },
    quoteVehicleConfiguration,
    findControlForField,
    findControlByLabel,
    setControlValue
  };

})();
